import React from 'react';
import { Box, Container, Typography, Paper, Divider, useTheme } from '@mui/material';
import { useLanguage } from '../contexts/LanguageContext';
import goldColors from '../themes/goldColors';

// Página de Términos y Condiciones de PCM Finanzas.
//
// Es una página "oculta": tiene ruta propia (/terminos-y-condiciones) y por
// eso es accesible por URL directa, pero a propósito NO se agregó ningún
// link hacia ella desde el menú público (PublicHeader) ni desde el footer
// (PublicFooter), así que no aparece navegando el sitio. Esto es útil para
// tenerla lista y poder enlazarla puntualmente (checkbox de registro, pie
// de un correo, requisito de una pasarela de pago, revisión de tiendas de
// apps, etc.) sin exponerla todavía en la navegación principal.
//
// Importante: este es un texto general/plantilla, no asesoría legal. Antes
// de publicarlo o exigir su aceptación a usuarios reales, conviene que un
// abogado en Perú lo revise y complete los datos societarios exactos
// (razón social, RUC, domicilio fiscal) donde se indica con corchetes.
const TermsAndConditionsPage = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const isEs = language !== 'en';

  const lastUpdated = isEs ? '27 de julio de 2026' : 'July 27, 2026';

  const Section = ({ title, children }) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: theme.palette.primary.main }}>
        {title}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }} component="div">
        {children}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ py: 8 }}>
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            fontWeight="bold"
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${goldColors.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
            }}
          >
            {isEs ? 'Términos y Condiciones' : 'Terms and Conditions'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEs ? 'Última actualización: ' : 'Last updated: '}
            {lastUpdated}
          </Typography>
        </Box>

        <Paper elevation={2} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
          {isEs ? (
            <>
              <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', lineHeight: 1.8 }}>
                Estos Términos y Condiciones ("Términos") regulan el acceso y uso de PCM Finanzas
                (el "Servicio"), una plataforma de planificación, control y monitoreo financiero
                operada por [Razón social de Darwin Optima] ("Darwin Optima", "nosotros"), con
                domicilio en Lima, Perú. Al crear una cuenta o utilizar el Servicio, aceptas estos
                Términos en su totalidad. Si no estás de acuerdo, no debes usar el Servicio.
              </Typography>

              <Divider sx={{ mb: 4 }} />

              <Section title="1. Descripción del Servicio">
                PCM Finanzas es una herramienta que permite registrar cuentas, ingresos, gastos,
                deudas, presupuestos y movimientos recurrentes, y generar reportes, calendarios y
                proyecciones a partir de esa información. El Servicio es una herramienta de apoyo
                a la gestión financiera personal o del negocio del usuario; no constituye asesoría
                financiera, contable, tributaria ni de inversión, y las proyecciones o cálculos
                mostrados son estimados que dependen enteramente de la exactitud de los datos que
                el usuario ingresa.
              </Section>

              <Section title="2. Registro y cuenta">
                Para usar el Servicio debes crear una cuenta proporcionando información veraz,
                completa y actualizada (nombre, correo electrónico, teléfono y contraseña). Eres
                responsable de mantener la confidencialidad de tus credenciales de acceso y de
                todas las actividades realizadas desde tu cuenta. Debes notificarnos de inmediato
                ante cualquier uso no autorizado de tu cuenta.
              </Section>

              <Section title="3. Uso aceptable">
                Te comprometes a usar el Servicio de forma lícita y a no: (a) intentar acceder sin
                autorización a otras cuentas, sistemas o datos; (b) realizar ingeniería inversa,
                copiar o revender el Servicio; (c) subir datos falsos con intención de defraudar a
                terceros; (d) usar el Servicio para actividades ilegales, incluyendo lavado de
                activos u ocultamiento patrimonial; ni (e) interferir con la operación normal de la
                plataforma (por ejemplo, mediante ataques automatizados o sobrecarga del sistema).
              </Section>

              <Section title="4. Planes, suscripciones y pagos">
                El Servicio puede ofrecerse mediante planes gratuitos y planes de pago, con las
                características y tarifas indicadas en la sección de Precios vigente al momento de
                la contratación. Las tarifas se cobran de forma anticipada según la periodicidad
                elegida (mensual o anual) y no son reembolsables salvo que la ley peruana aplicable
                disponga lo contrario. Podemos modificar los precios de los planes con aviso previo
                razonable; el cambio aplicará a partir del siguiente ciclo de facturación.
              </Section>

              <Section title="5. Tus datos financieros">
                Los datos financieros que registras (cuentas, montos, descripciones, deudas, etc.)
                son de tu propiedad. Los usamos únicamente para prestarte el Servicio: mostrarte tus
                reportes, calendarios y cálculos, y para fines de soporte técnico cuando lo
                autorices. No vendemos tus datos financieros a terceros. El tratamiento de tus datos
                personales se rige por nuestra Política de Privacidad y por la Ley N.º 29733, Ley de
                Protección de Datos Personales del Perú, y su reglamento.
              </Section>

              <Section title="6. Propiedad intelectual">
                El software, diseño, marca "PCM Finanzas", logotipos y contenidos del Servicio son
                propiedad de Darwin Optima o de sus licenciantes y están protegidos por la
                legislación de propiedad intelectual aplicable. Estos Términos no te otorgan
                ningún derecho sobre esas marcas o contenidos, más allá de la licencia limitada,
                personal, no exclusiva e intransferible para usar el Servicio conforme a estos
                Términos.
              </Section>

              <Section title="7. Disponibilidad del Servicio">
                Hacemos esfuerzos razonables para mantener el Servicio disponible, pero no
                garantizamos que funcione de forma ininterrumpida o libre de errores. Podemos
                suspender el acceso temporalmente por mantenimiento, actualizaciones o causas de
                fuerza mayor. Te recomendamos conservar copias o respaldos de tu información
                financiera crítica.
              </Section>

              <Section title="8. Limitación de responsabilidad">
                En la máxima medida permitida por la ley peruana, Darwin Optima no será responsable
                por decisiones financieras que tomes basándote en la información del Servicio, ni
                por daños indirectos, lucro cesante o pérdida de datos derivados del uso o
                imposibilidad de uso del Servicio. Nada en esta cláusula limita responsabilidades
                que no puedan excluirse conforme a la ley aplicable (por ejemplo, en casos de dolo o
                culpa inexcusable).
              </Section>

              <Section title="9. Suspensión y terminación">
                Podemos suspender o cancelar tu cuenta si incumples estos Términos, por
                inactividad prolongada, o por requerimiento legal. Puedes cancelar tu cuenta en
                cualquier momento desde la configuración de tu perfil o solicitándolo por escrito;
                la cancelación no genera reembolso de periodos ya pagados, salvo que la ley disponga
                lo contrario.
              </Section>

              <Section title="10. Modificaciones a estos Términos">
                Podemos actualizar estos Términos para reflejar cambios legales, operativos o del
                Servicio. Publicaremos la versión vigente en esta misma página con su fecha de
                actualización. El uso continuado del Servicio después de una modificación implica
                la aceptación de los nuevos Términos.
              </Section>

              <Section title="11. Ley aplicable y jurisdicción">
                Estos Términos se rigen por las leyes de la República del Perú. Cualquier
                controversia derivada de estos Términos se someterá a los jueces y tribunales del
                distrito judicial de Lima, Perú, salvo que la normativa de protección al consumidor
                establezca un fuero distinto en tu beneficio.
              </Section>

              <Section title="12. Contacto">
                Si tienes preguntas sobre estos Términos, puedes escribirnos a{' '}
                <strong>soporte@darwinoptima.com</strong> [confirmar correo de contacto real antes
                de publicar esta página].
              </Section>
            </>
          ) : (
            <>
              <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', lineHeight: 1.8 }}>
                These Terms and Conditions ("Terms") govern access to and use of PCM Finanzas (the
                "Service"), a financial planning, control and monitoring platform operated by
                [Darwin Optima legal entity name] ("Darwin Optima", "we"), based in Lima, Peru. By
                creating an account or using the Service, you accept these Terms in full. If you do
                not agree, you must not use the Service.
              </Typography>

              <Divider sx={{ mb: 4 }} />

              <Section title="1. Description of the Service">
                PCM Finanzas lets you record accounts, income, expenses, debts, budgets and
                recurring transactions, and generate reports, calendars and projections from that
                data. The Service is a support tool for personal or business financial management;
                it does not constitute financial, accounting, tax or investment advice, and any
                projections or calculations shown are estimates that depend entirely on the
                accuracy of the data you enter.
              </Section>

              <Section title="2. Registration and account">
                To use the Service you must create an account providing accurate, complete and
                up-to-date information (name, email, phone and password). You are responsible for
                keeping your login credentials confidential and for all activity under your
                account. You must notify us immediately of any unauthorized use of your account.
              </Section>

              <Section title="3. Acceptable use">
                You agree to use the Service lawfully and not to: (a) attempt unauthorized access to
                other accounts, systems or data; (b) reverse-engineer, copy or resell the Service;
                (c) upload false data intended to defraud third parties; (d) use the Service for
                illegal activity, including money laundering or asset concealment; or (e) interfere
                with the normal operation of the platform (e.g., through automated attacks or
                system overload).
              </Section>

              <Section title="4. Plans, subscriptions and payments">
                The Service may be offered through free and paid plans, with features and pricing as
                shown on the Pricing page at the time of purchase. Fees are billed in advance based
                on the chosen billing cycle (monthly or annual) and are non-refundable except as
                required by applicable Peruvian law. We may change plan pricing with reasonable
                prior notice; changes take effect from the next billing cycle.
              </Section>

              <Section title="5. Your financial data">
                The financial data you record (accounts, amounts, descriptions, debts, etc.) belongs
                to you. We use it only to provide the Service: showing you your reports, calendars
                and calculations, and for technical support when you authorize it. We do not sell
                your financial data to third parties. Processing of your personal data is governed
                by our Privacy Policy and by Peru's Law No. 29733, Personal Data Protection Law, and
                its regulations.
              </Section>

              <Section title="6. Intellectual property">
                The software, design, "PCM Finanzas" brand, logos and Service content are owned by
                Darwin Optima or its licensors and are protected by applicable intellectual property
                law. These Terms do not grant you any rights to those marks or content beyond a
                limited, personal, non-exclusive, non-transferable license to use the Service under
                these Terms.
              </Section>

              <Section title="7. Service availability">
                We make reasonable efforts to keep the Service available but do not guarantee
                uninterrupted or error-free operation. We may temporarily suspend access for
                maintenance, updates, or events beyond our control. We recommend keeping backups of
                your critical financial information.
              </Section>

              <Section title="8. Limitation of liability">
                To the maximum extent permitted by Peruvian law, Darwin Optima will not be liable
                for financial decisions you make based on information from the Service, nor for
                indirect damages, lost profits, or data loss arising from use or inability to use
                the Service. Nothing in this clause limits liability that cannot be excluded under
                applicable law.
              </Section>

              <Section title="9. Suspension and termination">
                We may suspend or cancel your account if you breach these Terms, due to prolonged
                inactivity, or as required by law. You may cancel your account at any time from your
                profile settings or by written request; cancellation does not generate a refund for
                already-paid periods, except as required by law.
              </Section>

              <Section title="10. Changes to these Terms">
                We may update these Terms to reflect legal, operational, or Service changes. The
                current version will be posted on this page with its update date. Continued use of
                the Service after a change means you accept the new Terms.
              </Section>

              <Section title="11. Governing law and jurisdiction">
                These Terms are governed by the laws of the Republic of Peru. Any dispute arising
                from these Terms will be submitted to the courts of the judicial district of Lima,
                Peru, unless consumer-protection rules establish a different venue in your favor.
              </Section>

              <Section title="12. Contact">
                If you have questions about these Terms, write to us at{' '}
                <strong>soporte@darwinoptima.com</strong> [confirm the real contact email before
                publishing this page].
              </Section>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsAndConditionsPage;
